/**
 * inboundApi.ts — Wrapper für die Empfangspostfach-Callables (Loop A).
 *
 * Muster: src/api/webhooksApi.ts (httpsCallable via firebase/functions, gleiche
 * europe-west3-Instanz aus firebaseConfig.ts) + `normalizeCallableError`, damit
 * Aufrufer den Function-Error-Code (z.B. `failed-precondition` bei
 * `feature_disabled`/`avv_version_stale`, `already-exists` bei `slug_taken`)
 * unterscheiden können, ohne die Firebase-SDK-Fehlerform zu leaken.
 *
 * Response-/Request-Shapes müssen EXAKT mit
 * `functions/src/inbound/provisioning.ts` übereinstimmen (Quelle der Wahrheit).
 *
 * WL-A4 legt hier nur die Provisionierungs-Aufrufe an; spätere Wellen (WL-A6/A8)
 * erweitern dieses Modul um `ingestInboundUpload` / `getInboundDocumentFile` /
 * `requestPortabilityExport`.
 *
 * @module inboundApi
 */

import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db, auth } from '../firebaseConfig';
import logger from '../utils/logger';
import { INBOUND_AVV_VERSION } from '../config/inboundAccess';
import type { InboundTaxDecision } from '../types/inboundTypes';

// ---------------------------------------------------------------------------
// Shapes (müssen exakt mit provisioning.ts übereinstimmen)
// ---------------------------------------------------------------------------

/** Grund, warum ein Slug nicht verfügbar ist (dryRun). */
export type SlugUnavailableReason = 'taken' | 'reserved' | 'invalid';

/** dryRun-Antwort von `provisionInboundAddress`. */
export interface SlugAvailability {
  available: boolean;
  reason?: SlugUnavailableReason;
}

/** Erfolgs-Antwort der echten Provisionierung. */
export interface ProvisionResult {
  slug: string;
  address: string;
}

/** Eingabe für die echte Provisionierung. */
export interface ProvisionInput {
  slug: string;
  avvVersion: string;
  isProfessionalSecrecyHolder: boolean;
  s203Accepted?: boolean;
}

// ---------------------------------------------------------------------------
// Callable-Referenzen
// ---------------------------------------------------------------------------

interface ProvisionCallableInput {
  slug: string;
  dryRun?: boolean;
  avvVersion: string;
  avvAccepted: true;
  isProfessionalSecrecyHolder: boolean;
  s203Accepted?: boolean;
}

const provisionInboundAddressFn = httpsCallable<
  ProvisionCallableInput,
  SlugAvailability | ProvisionResult
>(functions, 'provisionInboundAddress');

/** Request/response of `ingestInboundUpload` (§7.3). */
interface IngestUploadCallableInput {
  fileBase64: string;
  filename: string;
  mimeType: string;
}
interface IngestUploadCallableOutput {
  inboundDocumentId: string;
  deferred: boolean;
}
const ingestInboundUploadFn = httpsCallable<
  IngestUploadCallableInput,
  IngestUploadCallableOutput
>(functions, 'ingestInboundUpload');

/** Request/response of `getInboundDocumentFile` (§7.5). */
export type InboundArtifact = 'original' | 'eml' | 'validationReport';
interface GetInboundFileCallableInput {
  docId: string;
  artifact: InboundArtifact;
}
/** Discriminated result: small files inline, large files via signed URL. */
export type InboundFileResult =
  | { mode: 'inline'; base64: string; contentType: string; fileName: string }
  | {
      mode: 'signedUrl';
      url: string;
      expiresAt: number;
      contentType: string;
      fileName: string;
    };
const getInboundDocumentFileFn = httpsCallable<
  GetInboundFileCallableInput,
  InboundFileResult
>(functions, 'getInboundDocumentFile');

/** Request/response of `requestPortabilityExport` (§7.6). v1: no input fields. */
interface RequestExportCallableOutput {
  jobId: string;
}
const requestPortabilityExportFn = httpsCallable<
  Record<string, never>,
  RequestExportCallableOutput
>(functions, 'requestPortabilityExport');

/**
 * Request/response of `setInboundTaxDecision` (manuelle Steuerkontierung, §5.1).
 * Muss EXAKT mit `functions/src/inbound/setInboundTaxDecision.ts` übereinstimmen.
 */
export type InboundTaxDecisionAction = 'confirm' | 'override' | 'revoke';
/** Wählbare Override-Ziele (Allowlist §4.1) — Spiegel der Functions-SoT. */
export type SelectableTaxScheme =
  | 'standard'
  | 'reverse_charge'
  | 'intra_community'
  | 'tax_exempt'
  | 'zero_rated'
  | 'export'
  | 'out_of_scope';
interface SetTaxDecisionCallableInput {
  docId: string;
  action: InboundTaxDecisionAction;
  scheme?: SelectableTaxScheme;
  taxRateHint?: 0 | 7 | 19 | null;
  note?: string;
}
interface SetTaxDecisionCallableOutput {
  taxDecision: InboundTaxDecision | null;
}
const setInboundTaxDecisionFn = httpsCallable<
  SetTaxDecisionCallableInput,
  SetTaxDecisionCallableOutput
>(functions, 'setInboundTaxDecision');

// ---------------------------------------------------------------------------
// Fehler-Normalisierung (Muster webhooksApi.ts)
// ---------------------------------------------------------------------------

/**
 * Mappt einen Firebase-Callable-Fehler auf einen `Error` mit angehängtem `.code`
 * (z.B. `failed-precondition`, `already-exists`, `invalid-argument`). Aufrufer
 * unterscheiden so „Feature noch nicht aktiv" / „Slug vergeben" von echten
 * Fehlern, ohne die SDK-Fehlerform zu leaken.
 */
export function normalizeCallableError(
  err: unknown,
): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}

// ---------------------------------------------------------------------------
// Öffentliche API
// ---------------------------------------------------------------------------

/**
 * Prüft (ohne Wirkung) die Verfügbarkeit eines Slugs für das Empfangspostfach.
 *
 * @param slug Kandidaten-Slug (3–30 Zeichen, `[a-z0-9-]`).
 * @returns `{available, reason?}` — reason `'invalid'|'reserved'|'taken'`.
 * @throws Error (ggf. mit `.code`) — z.B. `failed-precondition` bei
 *   deaktiviertem Feature (`feature_disabled`).
 */
export async function checkInboundSlug(slug: string): Promise<SlugAvailability> {
  if (!slug || slug.trim() === '') {
    return { available: false, reason: 'invalid' };
  }
  try {
    const res = await provisionInboundAddressFn({
      slug,
      dryRun: true,
      avvVersion: INBOUND_AVV_VERSION,
      avvAccepted: true,
      isProfessionalSecrecyHolder: false,
    });
    return res.data as SlugAvailability;
  } catch (err) {
    logger.error('inboundApi.checkInboundSlug failed', err);
    throw normalizeCallableError(err);
  }
}

/**
 * Aktiviert das Empfangspostfach: vergibt den Slug, seedet die Mailbox und loggt
 * die AVV-Zustimmung (Consent). `avvAccepted` ist immer `true` — der Aufrufer ruft
 * diese Funktion NUR nach ausdrücklicher (unvorbelegter) Nutzer-Zustimmung auf.
 *
 * @param input Slug + AVV-Version + §203-Selbstauskunft.
 * @returns `{slug, address}`.
 * @throws Error (ggf. mit `.code`) — u.a. `already-exists` (slug_taken),
 *   `failed-precondition` (mailbox_exists | avv_version_stale | s203_not_available |
 *   email_not_verified | feature_disabled), `invalid-argument` (slug_invalid | slug_reserved).
 */
export async function provisionInboundAddress(
  input: ProvisionInput,
): Promise<ProvisionResult> {
  try {
    const res = await provisionInboundAddressFn({
      slug: input.slug,
      avvVersion: input.avvVersion,
      avvAccepted: true,
      isProfessionalSecrecyHolder: input.isProfessionalSecrecyHolder,
      ...(input.s203Accepted !== undefined
        ? { s203Accepted: input.s203Accepted }
        : {}),
    });
    return res.data as ProvisionResult;
  } catch (err) {
    logger.error('inboundApi.provisionInboundAddress failed', err);
    throw normalizeCallableError(err);
  }
}

/** Ergebnis eines manuellen Server-WORM-Uploads. */
export interface IngestUploadResult {
  inboundDocumentId: string;
  deferred: boolean;
}

/**
 * Lädt eine Datei über die Server-WORM-Pipeline (`ingestInboundUpload`) hoch —
 * der `INBOUND_ENABLED`-on-Zweig der Upload-Weiche (§5.11). Schreibt in
 * `inbound_documents` (KEIN `received_invoices`-Write, KEIN Client-Storage).
 *
 * @param fileBase64 base64 der Datei OHNE data-URL-Präfix.
 * @param filename Original-Dateiname.
 * @param mimeType gemeldeter MIME-Typ.
 * @returns `{inboundDocumentId, deferred}` — `deferred:true` = archiviert, Auswertung folgt.
 * @throws Error (ggf. mit `.code`) — z.B. `failed-precondition`
 *   (feature_disabled | mailbox_not_provisioned), `resource-exhausted`
 *   (fair_use_daily), `invalid-argument`.
 */
export async function ingestInboundUpload(
  fileBase64: string,
  filename: string,
  mimeType: string,
): Promise<IngestUploadResult> {
  try {
    const res = await ingestInboundUploadFn({ fileBase64, filename, mimeType });
    return res.data;
  } catch (err) {
    logger.error('inboundApi.ingestInboundUpload failed', err);
    throw normalizeCallableError(err);
  }
}

/**
 * Lädt ein Original/EML/Prüfbericht-Artefakt eines `inbound_documents`-Belegs
 * über `getInboundDocumentFile` (§7.5) — im Tenant-Scope, server-autorisiert.
 * Kleine Dateien kommen inline (base64), große (> 9 MB) als Signed-URL.
 *
 * @param docId Doc-ID des Belegs.
 * @param artifact `'original'|'eml'|'validationReport'`.
 * @returns diskriminiertes Ergebnis (`mode:'inline'` | `mode:'signedUrl'`).
 * @throws Error (ggf. mit `.code`) — `failed-precondition` (feature_disabled),
 *   `permission-denied`, `not-found`, `invalid-argument`.
 */
export async function getInboundDocumentFile(
  docId: string,
  artifact: InboundArtifact = 'original',
): Promise<InboundFileResult> {
  try {
    const res = await getInboundDocumentFileFn({ docId, artifact });
    return res.data;
  } catch (err) {
    logger.error('inboundApi.getInboundDocumentFile failed', err);
    throw normalizeCallableError(err);
  }
}

/**
 * Löst ein `InboundFileResult` in eine öffnbare Browser-URL auf: bei `inline`
 * wird ein Blob-URL aus dem base64 gebaut, bei `signedUrl` die URL direkt
 * zurückgegeben. Der Aufrufer öffnet/lädt die URL und ruft `revoke()` (nur
 * bei Blobs relevant), sobald sie nicht mehr gebraucht wird.
 *
 * @param result Ergebnis von {@link getInboundDocumentFile}.
 * @returns `{url, fileName, revoke}` — `revoke` ist bei Signed-URLs ein No-Op.
 */
export function inboundFileResultToUrl(result: InboundFileResult): {
  url: string;
  fileName: string;
  revoke: () => void;
} {
  if (result.mode === 'signedUrl') {
    return { url: result.url, fileName: result.fileName, revoke: () => {} };
  }
  const byteChars = atob(result.base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: result.contentType });
  const url = URL.createObjectURL(blob);
  return { url, fileName: result.fileName, revoke: () => URL.revokeObjectURL(url) };
}

/** Ergebnis des Anstoßens eines Portabilitäts-Exports. */
export interface RequestExportResult {
  jobId: string;
}

/**
 * Stößt einen Portabilitäts-Export (DSGVO Art. 20 / [I-7], §7.6) für den eigenen
 * Tenant an: `requestPortabilityExport` legt einen Job (`inbound_export_jobs`) an
 * und enqueued den Build-Task. Der Aufrufer beobachtet den Fortschritt über einen
 * Firestore-Listener auf den Job (queued → running → done) und lädt die fertige
 * ZIP über die im Job hinterlegte Signed-URL. Rate-Limit: 1 Export / 24 h.
 *
 * @returns `{jobId}` — die Doc-ID des angelegten Jobs.
 * @throws Error (ggf. mit `.code`) — `failed-precondition` (feature_disabled |
 *   mailbox_not_provisioned), `resource-exhausted` (one_export_per_day).
 */
export async function requestPortabilityExport(): Promise<RequestExportResult> {
  try {
    const res = await requestPortabilityExportFn({});
    return res.data;
  } catch (err) {
    logger.error('inboundApi.requestPortabilityExport failed', err);
    throw normalizeCallableError(err);
  }
}

/** Eingabe für {@link setInboundTaxDecision}. */
export interface SetInboundTaxDecisionInput {
  docId: string;
  action: InboundTaxDecisionAction;
  /** Pflicht bei `action==='override'`, sonst weglassen. */
  scheme?: SelectableTaxScheme;
  /** Optionaler Anzeige-Hinweis {0,7,19} — nie Berechnungsgrundlage (E-2). */
  taxRateHint?: 0 | 7 | 19 | null;
  /** Begründung; Pflicht (nicht leer) beim Weg-Override von §13b/IG (server-enforced). */
  note?: string;
}

/**
 * Setzt/bestätigt/hebt die manuelle Steuerkontierung eines Eingangsbelegs
 * (`setInboundTaxDecision`, §5.1). Owner-only v1. Der Server schreibt append-only
 * + denormalisiert das Summary; diese Funktion liefert den neuen Summary-Stand
 * (`null` nach `revoke`).
 *
 * @param input Doc-ID + Aktion (+ scheme/note/taxRateHint je nach Aktion).
 * @returns `{taxDecision}` — der neue Summary-Stand oder `null`.
 * @throws Error (ggf. mit `.code`) — `failed-precondition` (feature_disabled |
 *   document_not_processed | no_active_decision | confirm_unknown | note_required),
 *   `permission-denied`, `invalid-argument`.
 */
export async function setInboundTaxDecision(
  input: SetInboundTaxDecisionInput,
): Promise<{ taxDecision: InboundTaxDecision | null }> {
  try {
    const res = await setInboundTaxDecisionFn({
      docId: input.docId,
      action: input.action,
      ...(input.scheme !== undefined ? { scheme: input.scheme } : {}),
      ...(input.taxRateHint !== undefined ? { taxRateHint: input.taxRateHint } : {}),
      ...(input.note !== undefined ? { note: input.note } : {}),
    });
    return { taxDecision: res.data?.taxDecision ?? null };
  } catch (err) {
    logger.error('inboundApi.setInboundTaxDecision failed', err);
    throw normalizeCallableError(err);
  }
}

/** Schlanke Sicht auf `inbound_mailboxes/{uid}` für die Aktivierungs-Sektion. */
export interface InboundMailboxState {
  slug: string;
  address: string;
  status: string;
}

/**
 * Liest den eigenen Mailbox-Zustand direkt aus Firestore (Rule: get nur durch den
 * Owner). Liefert `null`, wenn noch keine Mailbox provisioniert wurde.
 *
 * @returns `{slug, address, status}` oder `null`.
 * @throws Error, wenn kein Nutzer eingeloggt ist oder der Read fehlschlägt.
 */
export async function getOwnInboundMailbox(): Promise<InboundMailboxState | null> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('inboundApi: Kein angemeldeter Nutzer.');
  }
  const snap = await getDoc(doc(db, 'inbound_mailboxes', user.uid));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<InboundMailboxState>;
  if (!data.slug || !data.address) return null;
  return {
    slug: data.slug,
    address: data.address,
    status: typeof data.status === 'string' ? data.status : 'active',
  };
}
